import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Users, BookOpen, IndianRupee, GraduationCap, TrendingUp, Trash2, Calendar as CalIcon, Edit3, Plus, UserPlus, Video, Bell, ExternalLink, FileText, Upload, CalendarDays, Share2, Copy, MessageCircle, Mail, Radio, Eye, Tag, Percent, RefreshCw, Activity, Trophy } from "lucide-react";
import {
  StatusChip, StatCard,
  CreateBatchDialog, EditBatchDialog,
  ManualEnrollPanel,
  CreateClassDialog, EditClassDialog, BulkScheduleDialog, NotesUploadButton, ShareClassButton,
  EditUserDialog, EditEnrollmentDialog,
  EditCourseDialog,
  WebinarFormDialog, WebinarRegistrationsDialog,
  CouponFormDialog,
} from "@/components/admin";
import { StudentDetailDialog } from "@/components/admin/StudentDetailDialog";
import { ExcelImportDialog } from "@/components/admin/ExcelImportDialog";
import { CurriculumDialog } from "@/components/admin/CurriculumDialog";
import { CoursePageBuilderDialog } from "@/components/admin/CoursePageBuilderDialog";
import { WebinarSeriesDialog, WebinarAnalyticsButton } from "@/components/admin/WebinarSeriesDialog";
import ClassResourcesButton from "@/components/admin/ClassResourcesButton";
import { AnnouncementBroadcast, DiscussionThread, AnalyticsPanel } from "@/components/engagement/EngagementComponents";
import { AIQuizGeneratorButton, AISummarizeWidget, AdminChallengesButton, QRAttendanceButton, AutoBatchSuggestions, GranularAnalyticsPanel } from "@/components/sprintc/SprintCComponents";
import LeadsAdminPanel from "@/components/admin/LeadsAdminPanel";
import FacultyAdminPanel from "@/components/admin/FacultyAdminPanel";
import WallOfFamePanel from "@/components/admin/WallOfFamePanel";
import { getCoursePricing, formatINR } from "@/lib/pricing";
import BackgroundJobsPanel from "@/components/admin/BackgroundJobsPanel";
import RevenueGoalWidget from "@/components/admin/RevenueGoalWidget";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [classes, setClasses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [zoomStatus, setZoomStatus] = useState(null);
  const [editBatch, setEditBatch] = useState(null);
  const [editClass, setEditClass] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [studentDetailId, setStudentDetailId] = useState(null);
  const [editEnrollment, setEditEnrollment] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [webinars, setWebinars] = useState([]);
  const [editWebinar, setEditWebinar] = useState(null);
  const [showCreateWebinar, setShowCreateWebinar] = useState(false);
  const [viewRegs, setViewRegs] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [editCoupon, setEditCoupon] = useState(null);
  const [showCreateCoupon, setShowCreateCoupon] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [enrollFilter, setEnrollFilter] = useState("all");
  const [tab, setTab] = useState("batches");

  const reload = () => {
    // Each call has its own .catch — one slow/failing endpoint never crashes the dashboard.
    api.get("/admin/stats").then(({ data }) => setStats(data || {})).catch(() => {});
    api.get("/users").then(({ data }) => setUsers(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/teachers").then(({ data }) => setTeachers(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/courses").then(({ data }) => setCourses(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/batches").then(({ data }) => setBatches(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/classes").then(({ data }) => setClasses(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/payments").then(({ data }) => setPayments(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/admin/enrollments").then(({ data }) => setEnrollments(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/admin/zoom-status").then(({ data }) => setZoomStatus(data)).catch(() => {});
    api.get("/admin/enquiries").then(({ data }) => setEnquiries(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/admin/webinars").then(({ data }) => setWebinars(Array.isArray(data) ? data : [])).catch(() => {});
    api.get("/admin/coupons").then(({ data }) => setCoupons(Array.isArray(data) ? data : [])).catch(() => {});
  };
  useEffect(() => { reload(); }, []);

  const removeUser = async (id) => {
    if (!confirm("Delete this user? All their data will be removed.")) return;
    try { await api.delete(`/users/${id}`); toast.success("User deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const resetPassword = async (u) => {
    if (!confirm(`Reset password for ${u.name}? New password will be emailed.`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`, {});
      navigator.clipboard?.writeText(data.temp_password).catch(() => {});
      toast.success(`New password: ${data.temp_password} (copied + emailed)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeEnrollment = async (id) => {
    if (!confirm("Delete this enrollment? Student will lose access.")) return;
    try { await api.delete(`/admin/enrollments/${id}`); toast.success("Enrollment deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const markBalancePaid = async (e) => {
    const remainingINR = ((e.amount_remaining_paise || 0) / 100).toLocaleString("en-IN");
    const method = window.prompt(
      `Mark ₹${remainingINR} as paid for ${e.user_name}?\n\nMethod (cash / upi / bank / other):`,
      "cash",
    );
    if (!method) return;
    const note = window.prompt("Optional note (reference / txn id):", "") || "";
    try {
      await api.post(`/admin/enrollments/${e.id}/mark-balance-paid`, { method, note });
      toast.success(`Balance ₹${remainingINR} marked paid`);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to mark paid"); }
  };
  const previewBalanceReminder = async () => {
    const saved = localStorage.getItem("wa_preview_phone") || "";
    const phone = window.prompt(
      "Preview balance-due reminder — kis WhatsApp number par bhejna hai?\n\n10-digit Indian number (bina +91):",
      saved,
    );
    if (!phone) return;
    const cleaned = phone.replace(/\D/g, "").slice(-10);
    if (cleaned.length !== 10) {
      toast.error("Valid 10-digit phone chahiye");
      return;
    }
    localStorage.setItem("wa_preview_phone", cleaned);
    const sampleName = window.prompt("Student first name (preview):", "Rahul") || "Rahul";
    const sampleAmount = window.prompt("Pending amount in ₹ (preview):", "4000") || "4000";
    const sampleCourse = window.prompt("Course title (preview):", "Meta Ads Course") || "Meta Ads Course";
    const sampleDays = window.prompt("Days remaining to balance due:", "3") || "3";
    try {
      const { data } = await api.post("/admin/wa-test/balance_due", {
        phone: cleaned,
        args: [sampleName, sampleAmount, sampleCourse, sampleDays],
      });
      if (data?.success) {
        toast.success(`Preview sent to +91${cleaned} ✓`);
      } else {
        toast.error(data?.message || "WhatsApp send failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send preview");
    }
  };
  const removeBatch = async (id) => {
    if (!confirm("Delete this batch?")) return;
    try { await api.delete(`/batches/${id}`); toast.success("Batch deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const sendReminders = async (cls) => {
    try {
      const { data } = await api.post(`/classes/${cls.id}/send-reminders`);
      toast.success(`Reminders sent: ${data.sent}/${data.total} (${data.skipped} skipped)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeClass = async (id) => {
    if (!confirm("Delete this class? Zoom meeting will also be removed.")) return;
    try { await api.delete(`/classes/${id}`); toast.success("Class deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-dashboard">
      <div className="text-xs uppercase tracking-[0.18em] text-fuchsia-300">Admin Console</div>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold">Operations overview</h1>
        <button
          onClick={async () => {
            if (!confirm("Sync 6 real CWK courses with thumbnails? Safe — idempotent.")) return;
            try {
              const { data } = await api.post("/admin/sync-courses");
              toast.success(`Synced — ${data.updated} updated, ${data.created} created, ${data.deleted_legacy} legacy removed`);
              setTimeout(() => window.location.reload(), 1500);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Sync failed");
            }
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FF0066]/10 hover:bg-[#FF0066]/20 border border-[#FF0066]/40 text-[#FF0066] text-xs font-semibold transition"
          data-testid="admin-sync-courses-btn"
          title="Force re-sync the 6 real CWK courses + thumbnails (use after deploy)"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Sync Courses (post-deploy)
        </button>
      </div>

      {zoomStatus && (
        <div className={`mt-4 glass rounded-xl px-4 py-3 text-sm flex items-center gap-3 flex-wrap ${zoomStatus.connected ? 'border-fuchsia-400/30' : 'border-amber-400/30'}`} data-testid="zoom-status">
          <div className={`w-2.5 h-2.5 rounded-full ${zoomStatus.connected ? 'bg-fuchsia-400' : 'bg-amber-400'}`} />
          {zoomStatus.connected ? (
            <>
              <span>Zoom Pro <b className="text-fuchsia-300">connected</b> ({zoomStatus.mode}) — naye classes & webinars auto-create honge on Zoom.</span>
              <button
                onClick={async () => {
                  if (!confirm("Disconnect Zoom account?")) return;
                  try { await api.post("/admin/zoom-disconnect"); toast.success("Disconnected"); reload(); }
                  catch (e) { toast.error("Failed"); }
                }}
                className="ml-auto text-xs text-amber-300 hover:underline"
                data-testid="zoom-disconnect"
              >Disconnect</button>
            </>
          ) : zoomStatus.oauth_configured ? (
            <>
              <span>
                Zoom OAuth ready — connect karne ke liye click karein. <span className="text-[var(--text-mute)]">Redirect URI: <code className="text-fuchsia-300">{zoomStatus.redirect_uri}</code></span>
              </span>
              <a
                href={zoomStatus.auth_url}
                target="_blank"
                rel="noreferrer"
                className="btn-primary !py-1.5 text-xs ml-auto"
                data-testid="zoom-connect"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Connect Zoom
              </a>
            </>
          ) : (
            <span>
              Zoom not configured — <code className="text-amber-300">{(zoomStatus.missing || []).join(", ") || "missing config"}</code>. Tab tak Jitsi fallback chalega.
            </span>
          )}
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Users} label="Students" value={stats?.students ?? "—"} color="fuchsia" />
        <StatCard icon={GraduationCap} label="Teachers" value={stats?.teachers ?? "—"} color="purple" />
        <StatCard icon={BookOpen} label="Courses" value={stats?.courses ?? "—"} color="amber" />
        <StatCard icon={TrendingUp} label="Classes" value={stats?.classes ?? "—"} color="cyan" />
        <StatCard icon={IndianRupee} label="Revenue" value={stats?.revenue != null ? `₹${Number(stats.revenue).toLocaleString("en-IN")}` : "—"} color="rose" />
      </div>

      <div className="mt-6">
        <RevenueGoalWidget />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full mt-10">
        <TabsList className="bg-transparent border border-white/10 rounded-2xl p-2 flex flex-wrap gap-1.5 h-auto w-full justify-start">
          <TabsTrigger value="batches" data-testid="a-tab-batches"><CalIcon className="w-4 h-4 mr-1" /> Batches</TabsTrigger>
          <TabsTrigger value="leads" data-testid="a-tab-leads"><Tag className="w-4 h-4 mr-1" /> Leads · CRM</TabsTrigger>
          <TabsTrigger value="faculty" data-testid="a-tab-faculty"><GraduationCap className="w-4 h-4 mr-1" /> Faculty</TabsTrigger>
          <TabsTrigger value="classes" data-testid="a-tab-classes"><Video className="w-4 h-4 mr-1" /> Live Classes</TabsTrigger>
          <TabsTrigger value="webinars" data-testid="a-tab-webinars"><Radio className="w-4 h-4 mr-1" /> Webinars</TabsTrigger>
          <TabsTrigger value="enroll" data-testid="a-tab-enroll"><UserPlus className="w-4 h-4 mr-1" /> Manual Enroll</TabsTrigger>
          <TabsTrigger value="users" data-testid="a-tab-users">Users</TabsTrigger>
          <TabsTrigger value="enrollments" data-testid="a-tab-enrollments">Enrollments</TabsTrigger>
          <TabsTrigger value="courses" data-testid="a-tab-courses">Courses</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="a-tab-coupons"><Tag className="w-4 h-4 mr-1" /> Coupons</TabsTrigger>
          <TabsTrigger value="payments" data-testid="a-tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="a-tab-enquiries"><Bell className="w-4 h-4 mr-1" /> Enquiries{(enquiries || []).filter((e) => e.status === "new").length > 0 ? ` (${(enquiries || []).filter((e) => e.status === "new").length})` : ""}</TabsTrigger>
          <TabsTrigger value="gallery" data-testid="a-tab-gallery"><Eye className="w-4 h-4 mr-1" /> Student Gallery</TabsTrigger>
          <TabsTrigger value="wof" data-testid="a-tab-wof"><Trophy className="w-4 h-4 mr-1" /> Wall of Fame</TabsTrigger>
          <TabsTrigger value="sharing" data-testid="a-tab-sharing"><Share2 className="w-4 h-4 mr-1" /> Sharing</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="a-tab-analytics"><TrendingUp className="w-4 h-4 mr-1" /> Analytics</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="a-tab-jobs"><Activity className="w-4 h-4 mr-1" /> Jobs · WA</TabsTrigger>
        </TabsList>

        <TabsContent value="batches">
          <div className="flex justify-between items-center mb-3 gap-3 flex-wrap">
            <div className="text-sm text-[var(--text-dim)]">Batch-wise management — teachers, timing, meeting links, status.</div>
            <CreateBatchDialog courses={courses} teachers={teachers} onCreated={reload} />
          </div>
          <div className="glass rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="text-left text-xs uppercase tracking-wider text-[var(--text-mute)] bg-white/5">
                <tr>
                  <th className="px-4 py-3">Batch</th>
                  <th className="px-4 py-3">Course</th>
                  <th className="px-4 py-3">Teacher</th>
                  <th className="px-4 py-3">Timing</th>
                  <th className="px-4 py-3">Dates</th>
                  <th className="px-4 py-3">Seats</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Meeting</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {batches.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-[var(--text-mute)]">No batches yet. Create one above.</td></tr>}
                {batches.map((b) => (
                  <tr key={b.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`batch-row-${b.id}`}>
                    <td className="px-4 py-3 font-medium">{b.name}</td>
                    <td className="px-4 py-3 text-xs text-[var(--text-dim)]">{b.course_title}</td>
                    <td className="px-4 py-3 text-xs">{b.teacher_name || "—"}</td>
                    <td className="px-4 py-3 text-xs">{b.timing || "—"}</td>
                    <td className="px-4 py-3 text-xs">{b.start_date} → {b.end_date}</td>
                    <td className="px-4 py-3 text-xs"><span className="text-amber-300 font-semibold">{b.enrolled_count}</span>/{b.capacity}</td>
                    <td className="px-4 py-3 text-xs"><StatusChip s={b.status} /></td>
                    <td className="px-4 py-3 text-xs">
                      {b.meeting_link ? <a href={b.meeting_link} target="_blank" rel="noreferrer" className="text-fuchsia-300 hover:underline inline-flex items-center gap-1">{b.meeting_provider} <ExternalLink className="w-3 h-3" /></a> : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs flex gap-1">
                      <button onClick={() => setEditBatch(b)} className="p-1.5 rounded-md hover:bg-white/5" data-testid={`edit-batch-${b.id}`}><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => removeBatch(b.id)} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-300"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="leads">
          <div className="mb-3 text-sm text-[var(--text-dim)]">
            Live CRM — sab leads ek jagah. Stages update karein, WhatsApp/call seedha karein, filter & search by course/city/name.
          </div>
          <LeadsAdminPanel />
        </TabsContent>

        <TabsContent value="faculty">
          <div className="mb-3 text-sm text-[var(--text-dim)]">
            Manage faculty profiles shown on homepage. Drag priority, hide/show, mark featured. Public sees only safe fields — admin_notes & salary never leak.
          </div>
          <FacultyAdminPanel />
        </TabsContent>

        <TabsContent value="classes">
          <div className="flex justify-between items-center mb-3 gap-3 flex-wrap">
            <div className="text-sm text-[var(--text-dim)]">Live class schedule across all batches. Create / delete / send reminders / bulk-import 78-day DM schedule / Excel upload.</div>
            <div className="flex gap-2 flex-wrap">
              <AIQuizGeneratorButton />
              <AISummarizeWidget />
              <AdminChallengesButton onDone={reload} />
              <AnnouncementBroadcast />
              <CurriculumDialog courses={courses} batches={batches} onDone={reload} />
              <ExcelImportDialog courses={courses} batches={batches} onDone={reload} />
              <BulkScheduleDialog batches={batches} onDone={reload} />
              <CreateClassDialog courses={courses} batches={batches} teachers={teachers} onCreated={reload} />
            </div>
          </div>
          <AutoBatchSuggestions />
          <div className="glass rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="text-left text-xs uppercase tracking-wider text-[var(--text-mute)] bg-white/5">
                <tr><th className="px-4 py-3">Day</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Course</th><th className="px-4 py-3">When</th><th className="px-4 py-3">Provider</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {classes.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--text-mute)]">No classes scheduled — click &ldquo;Bulk import 78-day DM schedule&rdquo; or &ldquo;New class&rdquo;.</td></tr>}
                {classes.map((c) => (
                  <tr key={c.id} className="border-t border-white/5" data-testid={`class-row-${c.id}`}>
                    <td className="px-4 py-3 text-xs">{c.day_number ? `D${c.day_number}` : "—"}</td>
                    <td className="px-4 py-3"><div className="line-clamp-2 max-w-md">{c.title}</div></td>
                    <td className="px-4 py-3 text-xs text-[var(--text-dim)]">{c.course_title}</td>
                    <td className="px-4 py-3 text-xs">{new Date(c.scheduled_at).toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs"><span className={`px-2 py-1 rounded-full uppercase ${c.provider === "zoom" ? "bg-fuchsia-500/15 text-fuchsia-300" : "bg-amber-500/15 text-amber-300"}`}>{c.provider}</span></td>
                    <td className="px-4 py-3 text-xs"><StatusChip s={c.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1 flex-wrap justify-end">
                        <Link to={`/live/${c.id}`} className="btn-primary !py-1.5 text-xs" data-testid={`open-class-${c.id}`}><Video className="w-3.5 h-3.5" /> Start</Link>
                        {!c.zoom_meeting_id && !c.is_demo && (
                          <button onClick={async () => {
                            try { await api.post(`/classes/${c.id}/generate-zoom`); toast.success("Zoom meeting created"); reload(); }
                            catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
                          }} className="btn-ghost !py-1.5 text-xs" data-testid={`gen-zoom-${c.id}`}><Video className="w-3.5 h-3.5" /> Zoom</button>
                        )}
                        <ShareClassButton cls={c} />
                        <NotesUploadButton classId={c.id} />
                        <ClassResourcesButton classId={c.id} classTitle={c.title} />
                        <DiscussionThread classId={c.id} classTitle={c.title} />
                        <QRAttendanceButton classId={c.id} classTitle={c.title} />
                        <button onClick={() => sendReminders(c)} className="btn-ghost !py-1.5 text-xs" data-testid={`send-reminders-${c.id}`}><Bell className="w-3.5 h-3.5" /> Remind</button>
                        <button onClick={() => setEditClass(c)} className="p-1.5 rounded-md hover:bg-white/5" data-testid={`edit-class-${c.id}`} title="Edit"><Edit3 className="w-4 h-4" /></button>
                        <button onClick={() => removeClass(c.id)} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-300" data-testid={`delete-class-${c.id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="enroll">
          <ManualEnrollPanel courses={courses} batches={batches} onDone={reload} />
        </TabsContent>

        <TabsContent value="users">
          <div className="flex justify-between items-center mb-3 gap-3 flex-wrap">
            <div className="text-sm text-[var(--text-dim)]">Sabhi platform users — students, teachers, admins.</div>
            <button onClick={() => setTab("enroll")} className="btn-primary text-sm" data-testid="users-quick-enroll">
              <UserPlus className="w-4 h-4" /> + Add Student (Manual Enroll)
            </button>
          </div>
          <div className="glass rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="text-left text-xs uppercase tracking-wider text-[var(--text-mute)] bg-white/5">
                <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Joined</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={() => u.role === "student" && setStudentDetailId(u.id)} data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 font-medium">{u.name}</td>
                    <td className="px-4 py-3 text-[var(--text-dim)]">{u.email}</td>
                    <td className="px-4 py-3"><span className="px-2 py-1 rounded-full bg-fuchsia-500/10 text-fuchsia-300 text-xs uppercase">{u.role}</span></td>
                    <td className="px-4 py-3 text-xs text-[var(--text-mute)]">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex gap-1">
                        {u.role === "student" && <button onClick={() => setStudentDetailId(u.id)} className="p-1.5 rounded-md hover:bg-fuchsia-500/10 text-fuchsia-300" data-testid={`view-student-${u.id}`} title="View full student details"><Eye className="w-4 h-4" /></button>}
                        <button onClick={() => resetPassword(u)} className="btn-ghost !py-1.5 text-xs" data-testid={`reset-pwd-${u.id}`} title="Reset password">🔑</button>
                        <button onClick={() => setEditUser(u)} className="p-1.5 rounded-md hover:bg-white/5" data-testid={`edit-user-${u.id}`} title="Edit"><Edit3 className="w-4 h-4" /></button>
                        <button onClick={() => removeUser(u.id)} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-300" title="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="enrollments">
          <div className="flex justify-between items-center mb-3 gap-3 flex-wrap">
            <div className="text-sm text-[var(--text-dim)]">
              Sabhi student enrollments — paid (online) + manually added. Kisi naye student ko add karna hai? Right side se Manual Enroll kar lo →
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              <button
                onClick={previewBalanceReminder}
                className="px-3 py-1.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 border border-amber-400/30 text-amber-300 text-xs font-semibold inline-flex items-center gap-1.5"
                data-testid="preview-balance-reminder"
                title="Send a sample balance-due WhatsApp reminder to any number (to see what students receive)"
              >
                📱 Preview balance reminder
              </button>
              <select
                value={enrollFilter}
                onChange={(e) => setEnrollFilter(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white"
                data-testid="enroll-filter"
              >
                <option value="all">All ({enrollments.length})</option>
                <option value="partial">Partial / Balance due ({enrollments.filter((e) => e.payment_status === "partial" && (e.amount_remaining_paise || 0) > 0).length})</option>
                <option value="paid_full">Paid in full</option>
                <option value="overdue">Overdue</option>
              </select>
              <button onClick={() => setTab("enroll")} className="btn-primary text-sm shrink-0" data-testid="enrollments-quick-enroll">
                <UserPlus className="w-4 h-4" /> + Add Student / Manual Enroll
              </button>
            </div>
          </div>
          <div className="glass rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[950px]">
              <thead className="text-left text-xs uppercase tracking-wider text-[var(--text-mute)] bg-white/5">
                <tr><th className="px-4 py-3">Student</th><th className="px-4 py-3">Course</th><th className="px-4 py-3">Batch</th><th className="px-4 py-3">Mode</th><th className="px-4 py-3">Payment</th><th className="px-4 py-3">Enrolled</th><th className="px-4 py-3 text-right">Actions</th></tr>
              </thead>
              <tbody>
                {(() => {
                  const now = new Date();
                  const filtered = enrollments.filter((e) => {
                    const remaining = e.amount_remaining_paise || 0;
                    const isPartial = e.payment_status === "partial" && remaining > 0;
                    const isOverdue = isPartial && e.balance_due_at && new Date(e.balance_due_at) < now;
                    if (enrollFilter === "all") return true;
                    if (enrollFilter === "partial") return isPartial;
                    if (enrollFilter === "paid_full") return !isPartial;
                    if (enrollFilter === "overdue") return isOverdue;
                    return true;
                  });
                  if (filtered.length === 0) {
                    return <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--text-mute)]">No enrollments match the filter.</td></tr>;
                  }
                  return filtered.map((e) => {
                    const remainingPaise = e.amount_remaining_paise || 0;
                    const remainingINR = remainingPaise / 100;
                    const isPartial = e.payment_status === "partial" && remainingPaise > 0;
                    const isOverdue = isPartial && e.balance_due_at && new Date(e.balance_due_at) < now;
                    const dueStr = e.balance_due_at ? new Date(e.balance_due_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : null;
                    return (
                      <tr key={e.id} className="border-t border-white/5" data-testid={`enroll-row-${e.id}`}>
                        <td className="px-4 py-3"><div className="font-medium">{e.user_name}</div><div className="text-xs text-[var(--text-mute)]">{e.user_email}</div></td>
                        <td className="px-4 py-3 text-xs">{e.course_title}</td>
                        <td className="px-4 py-3 text-xs">{e.batch_name || "—"}</td>
                        <td className="px-4 py-3 text-xs"><span className="px-2 py-1 rounded-full bg-purple-500/10 text-purple-300 uppercase">{e.payment_mode}</span></td>
                        <td className="px-4 py-3 text-xs">
                          {isPartial ? (
                            <div className={`inline-flex flex-col gap-0.5 ${isOverdue ? "text-rose-300" : "text-amber-300"}`}>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase self-start ${isOverdue ? "bg-rose-500/15 border border-rose-400/30" : "bg-amber-500/15 border border-amber-400/30"}`}>
                                {isOverdue ? "Overdue" : "Partial"}
                              </span>
                              <span className="font-semibold">₹{remainingINR.toLocaleString("en-IN")} due</span>
                              {dueStr && <span className="text-[10px] text-white/50">by {dueStr}</span>}
                            </div>
                          ) : (
                            <span className="px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 uppercase text-[10px] font-bold">Paid full</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs">{new Date(e.enrolled_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex gap-1 items-center">
                            {isPartial && (
                              <button
                                onClick={() => markBalancePaid(e)}
                                className="px-2 py-1 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/30 text-emerald-300 text-[11px] font-semibold"
                                data-testid={`mark-paid-${e.id}`}
                                title="Mark balance as paid (cash / UPI / bank)"
                              >
                                Mark balance paid
                              </button>
                            )}
                            <button onClick={() => setEditEnrollment(e)} className="p-1.5 rounded-md hover:bg-white/5" data-testid={`edit-enroll-${e.id}`} title="Move to another batch"><Edit3 className="w-4 h-4" /></button>
                            <button onClick={() => removeEnrollment(e.id)} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-300" title="Remove access"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="courses">
          <div className="flex justify-between items-center mb-3 gap-2 flex-wrap">
            <div className="text-sm text-[var(--text-dim)]">
              {courses.length} courses · click any card to edit title, pricing, duration, badges & more.
            </div>
            <CoursePageBuilderDialog courses={courses} onDone={reload} />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((c) => {
              const { mrp, final, hasDiscount, percentOff } = getCoursePricing(c);
              return (
              <div key={c.id} className="glass rounded-2xl p-4 flex flex-col gap-2" data-testid={`course-card-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wider text-fuchsia-300">{c.category}</div>
                    <div className="font-display text-base font-semibold mt-1 line-clamp-2">{c.title}</div>
                  </div>
                  {c.badge && (
                    <span className="text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-400/30 rounded-full px-2 py-0.5 shrink-0">{c.badge}</span>
                  )}
                </div>
                <div className="text-sm flex items-baseline gap-2">
                  <span className="text-white font-semibold" data-testid={`admin-course-price-${c.id}`}>{formatINR(final, { showFree: false })}</span>
                  {hasDiscount && (
                    <>
                      <span className="text-xs text-white/40 line-through" data-testid={`admin-course-mrp-${c.id}`}>{formatINR(mrp, { showFree: false })}</span>
                      <span className="text-[10px] text-emerald-300 font-semibold">{percentOff}% off</span>
                    </>
                  )}
                </div>
                <div className="text-xs text-[var(--text-mute)] flex items-center gap-3">
                  <span>{c.enrolled_count || 0} enrolled</span>
                  <span>·</span>
                  <span>{c.duration_weeks} weeks</span>
                  {c.mode && <><span>·</span><span>{c.mode}</span></>}
                </div>
                <button
                  onClick={() => setEditCourse(c)}
                  className="mt-1 inline-flex items-center justify-center gap-1.5 bg-white/5 hover:bg-fuchsia-500/15 border border-white/10 hover:border-fuchsia-400/40 text-white text-xs font-medium rounded-lg px-3 py-1.5 transition"
                  data-testid={`edit-course-${c.id}`}
                >
                  <Edit3 className="w-3.5 h-3.5" /> Edit course
                </button>
              </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="coupons">
          <div className="flex justify-between items-center mb-3 gap-3 flex-wrap">
            <div className="text-sm text-[var(--text-dim)]">
              Discount coupons — students checkout pe code apply karke discounted price pe pay karke auto-enroll ho jayenge.
            </div>
            <button onClick={() => setShowCreateCoupon(true)} className="btn-primary text-sm" data-testid="open-create-coupon">
              <Plus className="w-4 h-4" /> New coupon
            </button>
          </div>
          <div className="glass rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="text-left text-xs uppercase tracking-wider text-[var(--text-mute)] bg-white/5">
                <tr>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Discount</th>
                  <th className="px-4 py-3">Applies to</th>
                  <th className="px-4 py-3">Usage</th>
                  <th className="px-4 py-3">Expires</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[var(--text-mute)]">Abhi koi coupon nahi hai. <button onClick={() => setShowCreateCoupon(true)} className="text-fuchsia-300 hover:underline">+ Create your first coupon</button></td></tr>}
                {coupons.map((c) => {
                  const applicable = (c.applicable_course_ids || []).length === 0
                    ? "All courses"
                    : c.applicable_course_ids.map((id) => courses.find((co) => co.id === id)?.title || "—").join(", ");
                  const expired = c.expires_at && new Date(c.expires_at) < new Date();
                  return (
                    <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`coupon-row-${c.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-mono font-semibold text-fuchsia-300">{c.code}</div>
                        {c.description && <div className="text-xs text-[var(--text-mute)] mt-0.5">{c.description}</div>}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {c.discount_type === "percent" ? (
                          <span className="inline-flex items-center gap-1"><Percent className="w-3 h-3" /> {c.discount_value}% off</span>
                        ) : (
                          <span>₹{c.discount_value} off</span>
                        )}
                        {c.max_discount && c.discount_type === "percent" && <div className="text-[var(--text-mute)]">Max ₹{c.max_discount}</div>}
                        {c.min_amount && <div className="text-[var(--text-mute)]">Min order ₹{c.min_amount}</div>}
                      </td>
                      <td className="px-4 py-3 text-xs max-w-[200px] truncate" title={applicable}>{applicable}</td>
                      <td className="px-4 py-3 text-xs">
                        <span className="font-semibold text-amber-300">{c.usage_count || 0}</span>
                        {c.usage_limit ? ` / ${c.usage_limit}` : " / ∞"}
                      </td>
                      <td className="px-4 py-3 text-xs">{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "Never"}</td>
                      <td className="px-4 py-3 text-xs">
                        {expired ? <span className="px-2 py-1 rounded-full bg-rose-500/15 text-rose-300 uppercase">Expired</span>
                          : c.active ? <span className="px-2 py-1 rounded-full bg-fuchsia-500/15 text-fuchsia-300 uppercase">Active</span>
                          : <span className="px-2 py-1 rounded-full bg-white/5 text-white/60 uppercase">Inactive</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1">
                          <button onClick={() => { navigator.clipboard?.writeText(c.code); toast.success(`Code "${c.code}" copied`); }} className="p-1.5 rounded-md hover:bg-white/5" title="Copy code" data-testid={`copy-coupon-${c.id}`}><Copy className="w-4 h-4" /></button>
                          <button onClick={() => setEditCoupon(c)} className="p-1.5 rounded-md hover:bg-white/5" title="Edit" data-testid={`edit-coupon-${c.id}`}><Edit3 className="w-4 h-4" /></button>
                          <button onClick={async () => {
                            if (!confirm(`Delete coupon "${c.code}"?`)) return;
                            try { await api.delete(`/admin/coupons/${c.id}`); toast.success("Coupon deleted"); reload(); }
                            catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                          }} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-300" title="Delete"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="payments">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-sm text-[var(--text-dim)]">All recorded payments — real Razorpay + manually-recorded admin entries.</div>
            <button
              onClick={async () => {
                if (!window.confirm("Demo / test payments delete karne hain?\n\nYeh sirf seeded demo accounts (student@codewithkamlesh.in, test_* emails, @example.com) ki payments + pay_mock_* IDs delete karega.\n\nReal Razorpay payments aur aapki manual admin entries SAFE rahengi.")) return;
                try {
                  const { data } = await api.post("/admin/cleanup-demo-data");
                  toast.success(`Cleaned: ${data.payments_deleted} demo + ${data.mock_payments_deleted} mock payments deleted`);
                  reload();
                } catch (e) { toast.error(e.response?.data?.detail || "Cleanup failed"); }
              }}
              className="inline-flex items-center gap-1.5 bg-red-500/15 hover:bg-red-500/25 border border-red-500/40 text-red-300 hover:text-red-200 font-medium rounded-lg px-3.5 py-1.5 text-xs transition"
              data-testid="cleanup-demo-btn"
              title="Removes seeded demo users' payments + mock-checkout entries. Production users untouched."
            >
              <Trash2 className="w-3.5 h-3.5" /> Cleanup demo / test data
            </button>
          </div>
          <div className="glass rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="text-left text-xs uppercase tracking-wider text-[var(--text-mute)] bg-white/5">
                <tr><th className="px-4 py-3">Order</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Mode</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Created</th></tr>
              </thead>
              <tbody>
                {payments.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[var(--text-mute)]">No payments yet.</td></tr>}
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-white/5">
                    <td className="px-4 py-3 font-mono text-xs">{p.order_id}</td>
                    <td className="px-4 py-3">₹{(p.amount / 100).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-xs">{p.payment_mode || (p.via === "manual" ? "manual" : "online")}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs ${p.status === "paid" ? "bg-fuchsia-500/15 text-fuchsia-300" : "bg-amber-500/15 text-amber-300"}`}>{p.status}</span></td>
                    <td className="px-4 py-3 text-xs text-[var(--text-mute)]">{new Date(p.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="enquiries">
          <div className="text-sm text-[var(--text-dim)] mb-3">Landing-page enquiries — track and convert leads.</div>
          <div className="glass rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="text-left text-xs uppercase tracking-wider text-[var(--text-mute)] bg-white/5">
                <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Interest</th><th className="px-4 py-3">Message</th><th className="px-4 py-3">When</th><th className="px-4 py-3">Status</th></tr>
              </thead>
              <tbody>
                {enquiries.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--text-mute)]">No enquiries yet.</td></tr>}
                {enquiries.map((q) => (
                  <tr key={q.id} className="border-t border-white/5" data-testid={`enq-row-${q.id}`}>
                    <td className="px-4 py-3 font-medium">{q.name}</td>
                    <td className="px-4 py-3 text-xs">
                      <div><a href={`mailto:${q.email}`} className="text-fuchsia-300 hover:underline">{q.email}</a></div>
                      <div><a href={`https://wa.me/${q.phone.replace(/\D/g,"")}`} target="_blank" rel="noreferrer" className="text-emerald-300 hover:underline">{q.phone}</a></div>
                    </td>
                    <td className="px-4 py-3 text-xs">{q.interest || "general"}</td>
                    <td className="px-4 py-3 text-xs max-w-xs"><div className="line-clamp-2">{q.message || "—"}</div></td>
                    <td className="px-4 py-3 text-xs text-[var(--text-mute)]">{new Date(q.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <select value={q.status} onChange={async (e) => {
                        try { await api.patch(`/admin/enquiries/${q.id}`, { status: e.target.value }); toast.success("Updated"); reload(); }
                        catch (er) { toast.error("Failed"); }
                      }} className="bg-[var(--bg-elev-1)] border border-white/10 rounded px-2 py-1 text-xs" data-testid={`enq-status-${q.id}`}>
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="converted">Converted ✓</option>
                        <option value="dropped">Dropped</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="webinars">
          <div className="flex justify-between items-center mb-3 gap-3 flex-wrap">
            <div className="text-sm text-[var(--text-dim)]">Webinar landing pages — schedule, edit, view registrations. Public URL: <code className="text-fuchsia-300">/webinar/&lt;slug&gt;</code></div>
            <div className="flex gap-2 flex-wrap">
              <WebinarSeriesDialog onDone={reload} />
              <button onClick={() => setShowCreateWebinar(true)} className="btn-primary text-sm" data-testid="open-create-webinar"><Plus className="w-4 h-4" /> New webinar</button>
            </div>
          </div>
          <div className="glass rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="text-left text-xs uppercase tracking-wider text-[var(--text-mute)] bg-white/5">
                <tr>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Topic</th>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Regs</th>
                  <th className="px-4 py-3">Zoom</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {webinars.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[var(--text-mute)]">No webinars yet. Click &ldquo;New webinar&rdquo;.</td></tr>}
                {webinars.map((w) => (
                  <tr key={w.id} className="border-t border-white/5" data-testid={`webinar-row-${w.id}`}>
                    <td className="px-4 py-3"><div className="font-medium line-clamp-1 max-w-xs">{w.title}</div><div className="text-[10px] text-[var(--text-mute)]">/webinar/{w.slug}</div></td>
                    <td className="px-4 py-3 text-xs uppercase">{w.topic}</td>
                    <td className="px-4 py-3 text-xs">{new Date(w.scheduled_at).toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs">{w.registration_count || 0}/{w.max_capacity || 500}</td>
                    <td className="px-4 py-3 text-xs">{w.zoom_meeting_id ? "✓ " + (w.is_webinar_actual ? "WEB" : "MTG") : "—"}</td>
                    <td className="px-4 py-3 text-xs"><StatusChip s={w.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1 flex-wrap justify-end">
                        {w.zoom_start_url ? (
                          <a href={w.zoom_start_url} target="_blank" rel="noreferrer" className="btn-primary !py-1.5 text-xs" data-testid={`start-webinar-${w.id}`} title="Start as host"><Radio className="w-3.5 h-3.5" /> Start</a>
                        ) : w.zoom_join_url ? (
                          <a href={w.zoom_join_url} target="_blank" rel="noreferrer" className="btn-primary !py-1.5 text-xs" data-testid={`start-webinar-${w.id}`} title="Join (host URL not available — using join URL)"><Radio className="w-3.5 h-3.5" /> Start</a>
                        ) : null}
                        <a href={`/webinar/${w.slug}`} target="_blank" rel="noreferrer" className="btn-ghost !py-1.5 text-xs" data-testid={`view-webinar-${w.id}`}><ExternalLink className="w-3.5 h-3.5" /> View</a>
                        <button onClick={() => setViewRegs(w)} className="btn-ghost !py-1.5 text-xs" title="Registrations"><Eye className="w-3.5 h-3.5" /> Regs</button>
                        <WebinarAnalyticsButton webinar={w} />
                        <button onClick={() => setEditWebinar(w)} className="p-1.5 rounded-md hover:bg-white/5" title="Edit"><Edit3 className="w-4 h-4" /></button>
                        <button onClick={async () => {
                          if (!confirm("Delete webinar + all registrations?")) return;
                          try { await api.delete(`/admin/webinars/${w.id}`); toast.success("Deleted"); reload(); }
                          catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
                        }} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-300" title="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="gallery">
          <StudentGalleryPanel />
        </TabsContent>

        <TabsContent value="wof">
          <WallOfFamePanel courses={courses} />
        </TabsContent>

        <TabsContent value="sharing">
          <SharingSettingsPanel />
        </TabsContent>

        <TabsContent value="analytics">
          <AnalyticsPanel />
          <div className="mt-4">
            <GranularAnalyticsPanel />
          </div>
        </TabsContent>

        <TabsContent value="jobs">
          <div className="mb-3 text-sm text-[var(--text-dim)]">
            Background jobs · APScheduler · WhatsApp + email automation. Force-run any job with &ldquo;Run Now&rdquo; — no waiting for the next interval. Auto-refreshes every 10 s.
          </div>
          <BackgroundJobsPanel />
        </TabsContent>
      </Tabs>

      <EditBatchDialog batch={editBatch} teachers={teachers} onClose={() => setEditBatch(null)} onSaved={reload} />
      <EditClassDialog cls={editClass} teachers={teachers} batches={batches} onClose={() => setEditClass(null)} onSaved={reload} />
      <EditUserDialog user={editUser} onClose={() => setEditUser(null)} onSaved={reload} />
      <StudentDetailDialog userId={studentDetailId} onClose={() => setStudentDetailId(null)} onChange={reload} />
      <EditEnrollmentDialog enroll={editEnrollment} batches={batches} onClose={() => setEditEnrollment(null)} onSaved={reload} />
      <WebinarFormDialog open={showCreateWebinar || !!editWebinar} webinar={editWebinar} courses={courses} onClose={() => { setShowCreateWebinar(false); setEditWebinar(null); }} onSaved={reload} />
      <WebinarRegistrationsDialog webinar={viewRegs} onClose={() => setViewRegs(null)} />
      <CouponFormDialog open={showCreateCoupon || !!editCoupon} coupon={editCoupon} courses={courses} onClose={() => { setShowCreateCoupon(false); setEditCoupon(null); }} onSaved={reload} />
      <EditCourseDialog course={editCourse} onClose={() => setEditCourse(null)} onSaved={reload} />
    </div>
  );
}

function StudentGalleryPanel() {
  const [items, setItems] = useState([]);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const load = () => api.get(`/admin/student-gallery?verified_only=${verifiedOnly}`).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(load, [verifiedOnly]);
  const verify = async (uid, kind, approved) => {
    try { await api.patch(`/admin/students/${uid}/verify-photo`, { kind, approved }); toast.success(approved ? "Approved" : "Marked pending"); load(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div data-testid="student-gallery-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-sm text-[var(--text-dim)]">Verify student photos (profile, cover, ID card, classroom). {items.length} students.</div>
        <label className="inline-flex items-center gap-1.5 text-xs text-white/70"><input type="checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} /> Show verified only</label>
      </div>
      {items.length === 0 && <div className="text-center text-white/40 py-12 text-sm">No student photos uploaded yet.</div>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <div key={s.id} className="glass rounded-2xl p-3 space-y-2" data-testid={`gallery-${s.id}`}>
            <div className="flex items-center gap-2">
              {s.profile_image && <img src={s.profile_image} className="w-12 h-12 rounded-full object-cover" />}
              <div className="flex-1 min-w-0"><div className="font-medium text-sm truncate">{s.name}</div><div className="text-[10px] text-white/40 truncate">{s.email}</div></div>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {["profile", "cover", "id_card", "classroom"].map((k) => {
                const field = { profile: "profile_image", cover: "cover_image", id_card: "id_card_image", classroom: "classroom_image" }[k];
                const url = s[field];
                const ver = s[`${field}_verified`];
                if (!url) return <div key={k} className="aspect-square rounded bg-white/5 flex items-center justify-center text-[8px] text-white/30 uppercase">{k}</div>;
                return (
                  <div key={k} className="aspect-square relative rounded overflow-hidden bg-black group">
                    <img src={url} className="w-full h-full object-cover" />
                    <div className="absolute top-0 right-0 text-[8px] px-1 py-0.5 bg-black/70">{ver ? "✅" : "⏳"}</div>
                    <button onClick={() => verify(s.id, k, !ver)} className="absolute bottom-0 inset-x-0 bg-[#FF0066]/90 text-white text-[9px] py-0.5 opacity-0 group-hover:opacity-100 transition" data-testid={`verify-${s.id}-${k}`}>{ver ? "Unverify" : "Approve"}</button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SharingSettingsPanel() {
  const [s, setS] = useState(null);
  const [shares, setShares] = useState([]);
  const load = () => {
    api.get("/sharing/settings").then(({ data }) => setS(data)).catch(() => {});
    api.get("/admin/shares").then(({ data }) => setShares(data)).catch(() => {});
  };
  useEffect(load, []);
  const save = async () => {
    try { await api.post("/admin/sharing/settings", s); toast.success("Settings saved"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this share globally?")) return;
    try { await api.delete(`/shares/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };
  if (!s) return <div className="text-white/40 py-8 text-sm">Loading…</div>;
  const toggles = [
    { k: "student_to_student", label: "Student → Student sharing" },
    { k: "teacher_to_student", label: "Teacher → Student sharing" },
    { k: "admin_to_batch", label: "Admin → Batch broadcast" },
    { k: "public_resources", label: "Public resources (anyone can post)" },
    { k: "allow_voice_notes", label: "Allow voice note sharing" },
  ];
  return (
    <div className="space-y-6" data-testid="sharing-settings-panel">
      <div className="glass rounded-2xl p-5">
        <div className="text-sm font-semibold mb-3">🔐 Sharing permissions</div>
        <div className="space-y-2">
          {toggles.map((t) => (
            <label key={t.k} className="flex items-center justify-between gap-3 p-2.5 rounded-lg hover:bg-white/[0.02] cursor-pointer">
              <span className="text-sm">{t.label}</span>
              <input type="checkbox" checked={!!s[t.k]} onChange={(e) => setS({ ...s, [t.k]: e.target.checked })} data-testid={`toggle-${t.k}`} className="w-5 h-5 accent-[#FF0066]" />
            </label>
          ))}
          <label className="flex items-center justify-between gap-3 p-2.5 rounded-lg">
            <span className="text-sm">Max file size (MB)</span>
            <input type="number" min="1" max="500" value={s.max_file_mb || 20} onChange={(e) => setS({ ...s, max_file_mb: Number(e.target.value) })} className="w-24 bg-[#0a0a0a] border border-white/10 rounded px-2 py-1 text-sm text-right" />
          </label>
        </div>
        <button onClick={save} className="mt-3 bg-[#FF0066] hover:bg-[#e6005c] text-white font-semibold rounded-full px-5 py-2 text-sm" data-testid="save-sharing-settings">Save settings</button>
      </div>

      <div className="glass rounded-2xl p-5">
        <div className="text-sm font-semibold mb-3">📤 All shared resources ({shares.length})</div>
        {shares.length === 0 && <div className="text-white/40 text-xs text-center py-6">No shares yet.</div>}
        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
          {shares.map((sh) => (
            <div key={sh.id} className="flex items-start gap-2 p-2.5 rounded-lg border border-white/10" data-testid={`admin-share-${sh.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{sh.title}</div>
                <div className="text-[10px] text-white/40">{sh.owner_name} ({sh.owner_role}) • {sh.type} → {sh.audience_kind} • {new Date(sh.created_at).toLocaleDateString()}</div>
                <a href={sh.url} target="_blank" rel="noreferrer" className="text-[11px] text-[#FF0066] truncate block hover:underline">{sh.url}</a>
              </div>
              <button onClick={() => del(sh.id)} className="p-1.5 rounded hover:bg-red-500/10 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

